import type { ICorridor } from "../shared/session/interfaces/session";

export interface CorridorPair {
  originCountryCode: string;
  destinationCountryCode: string;
}

/** Unique destination country codes from all link corridors (sorted). */
export const supportedDestinationCountries = (
  corridors: ICorridor[]
): string[] =>
  Array.from(new Set(corridors.map((c) => c.destinationCountryCode))).sort();

/**
 * Resolves the initial corridor from session config: pinned default, or the
 * first row in `config.corridors` (widget catalog order).
 */
export const resolveInitialCorridor = (
  corridors: ICorridor[],
  defaultCorridor?: CorridorPair
): CorridorPair | undefined => {
  if (defaultCorridor) return defaultCorridor;
  const first = corridors[0];
  if (!first) return undefined;
  return {
    originCountryCode: first.originCountryCode,
    destinationCountryCode: first.destinationCountryCode,
  };
};

/** Destination country seed when compose store is empty. */
export const seedDestinationCountry = (
  corridors: ICorridor[],
  defaultCorridor?: CorridorPair
): string | undefined =>
  resolveInitialCorridor(corridors, defaultCorridor)?.destinationCountryCode;

/**
 * Resolves the origin country for a destination. Prefers US when multiple
 * corridors share the same destination (matches backend request-flow strategy).
 */
export const resolveOriginForDestination = (
  corridors: ICorridor[],
  destinationCountryCode: string
): string | undefined => {
  const dest = destinationCountryCode.toUpperCase();
  const matches = corridors.filter(
    (c) => c.destinationCountryCode.toUpperCase() === dest
  );
  if (matches.length === 0) return undefined;
  const usMatch = matches.find((c) => c.originCountryCode === "US");
  return (usMatch ?? matches[0]).originCountryCode;
};

/** Lookup a single corridor row for an origin/destination pair. */
export const findCorridor = (
  corridors: ICorridor[],
  originCountryCode: string,
  destinationCountryCode: string
): ICorridor | null =>
  corridors.find(
    (c) =>
      c.originCountryCode === originCountryCode &&
      c.destinationCountryCode === destinationCountryCode
  ) ?? null;
